import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion } from "framer-motion";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileBusinessOpen, setMobileBusinessOpen] = useState(false);

  const [bannerVisible, setBannerVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${isScrolled
        ? "bg-amber-500 backdrop-blur-sm shadow-lg"
        : "bg-amber-500"
        }`}
    >
      {bannerVisible && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-[#0f172a] text-white py-2 px-8 shadow-md relative z-[60] border-b border-yellow-500/40"
        >
          <div className="container mx-auto flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] sm:text-xs md:text-sm font-bold tracking-wide text-center">
            <span className="flex items-center gap-1.5">
              <span className="hidden sm:inline">📢</span>
              {new Date() < new Date('2026-06-01') ? 'PRE-ORDER NOW:' : 'ORDER NOW:'} 
              <span className="text-yellow-400 whitespace-nowrap">"THE WAIT WORK"</span>
            </span>
            <Link 
              to="/books" 
              className="bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-500 transition-all text-[9px] sm:text-[10px] md:text-xs font-black uppercase shadow-[0_0_10px_rgba(202,138,4,0.3)] whitespace-nowrap"
              onClick={() => setBannerVisible(false)}
            >
              {new Date() < new Date('2026-06-01') ? 'Get Your Copy' : 'Order Now'}
            </Link>
            <button 
              onClick={() => setBannerVisible(false)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white p-1 transition-colors"
              aria-label="Close banner"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}

      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link
            to="/"
            className="text-2xl font-bold text-gray-900 hover:text-gray-700 transition-colors duration-300"
          >
            OYEKA Amara
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-900 p-2 rounded-lg hover:text-gray-700 transition-colors duration-300"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-1">
            <NavLink to="/about">About</NavLink>

            {/* Desktop Businesses Dropdown */}
            <div className="relative group">
              <button className="px-4 py-2 text-gray-900 hover:text-gray-700 transition-colors duration-300 flex items-center">
                Businesses
                <ChevronDown
                  size={16}
                  className="ml-1 transform group-hover:rotate-180 transition-transform duration-300"
                />
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                <div className="py-2 bg-white rounded-xl shadow-xl">
                  <BusinessLink
                    href="https://led2lead.com/"
                    text="Led2Leading Consulting Limited"
                  />
                  <BusinessLink
                    href="https://seagreen-media.com/"
                    text="Seagreen Media Limited"
                  />
                  <BusinessLink
                    href="https://geturu.com/"
                    text="Uru"
                  />
                </div>

              </div>
            </div>
            <NavLink to="/foundation">Foundation</NavLink>

            <NavLink to="/inthepress">In the Press</NavLink>
            <NavLink to="/books">My Book</NavLink>
            <NavLink to="/presentations">Presentations</NavLink>

            <NavLink to="/contact">Contact</NavLink>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden transition-all duration-300 ease-in-out ${menuOpen
            ? "max-h-screen opacity-100 visible"
            : "max-h-0 opacity-0 invisible overflow-hidden"
            }`}
        >
          <div className="py-4 space-y-2">
            <MobileNavLink to="/about" onClick={() => setMenuOpen(false)}>
              About
            </MobileNavLink>

            {/* Mobile Businesses Dropdown */}
            <div className="px-4">
              <button
                onClick={() => setMobileBusinessOpen(!mobileBusinessOpen)}
                className="w-full flex items-center justify-between text-gray-900 hover:text-gray-700 transition-colors duration-300 py-2"
              >
                <span>Businesses</span>
                <ChevronDown
                  size={16}
                  className={`transform transition-transform duration-300 ${mobileBusinessOpen ? "rotate-180" : ""
                    }`}
                />
              </button>
              <div
                className={`pl-4 space-y-2 transition-all duration-300 ${mobileBusinessOpen
                  ? "max-h-40 opacity-100 visible mt-2"
                  : "max-h-0 opacity-0 invisible overflow-hidden"
                  }`}
              >
                <a
                  href="https://signalhousegroup.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-gray-700 hover:text-gray-900 transition-colors duration-300 py-2"
                >
                  Led2Leading Consulting Limited
                </a>
                <a
                  href="https://seagreenmedia.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-gray-700 hover:text-gray-900 transition-colors duration-300 py-2"
                >
                  Seagreen Media Limited
                </a>
                <a
                  href="https://geturu.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-gray-700 hover:text-gray-900 transition-colors duration-300 py-2"
                >
                  Uru
                </a>
              </div>
            </div>
            <MobileNavLink to="/foundation" onClick={() => setMenuOpen(false)}>
              Foundation
            </MobileNavLink>
            <MobileNavLink to="/inthepress" onClick={() => setMenuOpen(false)}>
              In the Press
            </MobileNavLink>
            <MobileNavLink to="/books" onClick={() => setMenuOpen(false)}>
              My Book
            </MobileNavLink>
            <MobileNavLink to="/presentations" onClick={() => setMenuOpen(false)}>
              Presentations
            </MobileNavLink>
            <MobileNavLink to="/contact" onClick={() => setMenuOpen(false)}>
              Contact
            </MobileNavLink>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Helper Components
const NavLink = ({ to, children }) => (
  <Link
    to={to}
    className="px-4 py-2 text-gray-900 hover:text-gray-700 transition-colors duration-300"
  >
    {children}
  </Link>
);

const MobileNavLink = ({ to, children, onClick }) => (
  <Link
    to={to}
    className="block px-4 py-3 text-gray-900 hover:text-gray-700 transition-colors duration-300"
    onClick={onClick}
  >
    {children}
  </Link>
);

const BusinessLink = ({ href, text }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="block px-4 py-2 text-gray-900 hover:bg-amber-500 transition-colors duration-300"
  >
    {text}
  </a>
);

export default Navbar;